import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'si-check-box-group',
  imports: [CommonModule, MatCheckboxModule, TranslatePipe],
  templateUrl: './si-check-box-group.component.html',
  styleUrl: './si-check-box-group.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SiCheckBoxGroupComponent),
      multi: true
    }
  ]
})
export class SiCheckBoxGroupComponent implements ControlValueAccessor {

  @Input() options: string[] = [];
  @Input() isReadMode: boolean = true;
  @Input() isDisabled: boolean = false;
  @Input() columnsPerRow: 1 | 2 | 3 = 1;
  @Input() singleRow: boolean = false;

  private selectedValues: string[] = [];
  private formControlDisabled = false;

  private onModelChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly translate: TranslateService) {}

  get effectiveDisabled(): boolean {
    return this.isDisabled || this.formControlDisabled;
  }

  get selectedLabels(): string {
    if (!this.selectedValues.length) {
      return '';
    }

    return this.options
      .filter((option) => this.isChecked(option))
      .map((option) => this.translate.instant(option))
      .join(', ');
  }

  isChecked(value: string): boolean {
    const normalizedValue = value.toString();
 
    // Backward compatibility: supports direct value storage in selectedValues.
    if (this.selectedValues.some((selected) => selected.toString() === normalizedValue)) {
      return true;
    }
 
    // Supports index-based selection where selectedValues contains 1-based positions.
    const optionIndex = this.options.findIndex((option) => option.toString() === normalizedValue);
    if (optionIndex === -1) {
      return false;
    }
 
    const oneBasedIndex = optionIndex + 1;
    return this.selectedValues.some((selected) => {
      const parsed = Number(selected);
      return Number.isInteger(parsed) && parsed === oneBasedIndex;
    });
  }

  onOptionChange(event: MatCheckboxChange, optionValue: string): void {
    if (this.effectiveDisabled) {
      return;
    }

    if (event.checked) {
      if (!this.isChecked(optionValue)) {
        this.selectedValues = [...this.selectedValues, optionValue];
      }
    } else {
      // selectedValues may hold either the direct option value or its 1-based index, so remove both forms.
      const oneBasedIndex = this.options.findIndex((option) => option.toString() === optionValue.toString()) + 1;
      this.selectedValues = this.selectedValues.filter((item) => {
        if (item.toString() === optionValue.toString()) {
          return false;
        }
        const parsed = Number(item);
        return !(Number.isInteger(parsed) && parsed === oneBasedIndex);
      });
    }

    this.onModelChange([...this.selectedValues]);
    this.onTouched();
  }

  writeValue(value: string[] | null): void {
    this.selectedValues = Array.isArray(value) ? value.map((item) => item?.toString() ?? '') : [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDisabled = isDisabled;
  }

  trackByValue(_: number, option: string): string {
    return option;
  }

  get safeColumnsPerRow(): number {
    return this.columnsPerRow >= 1 && this.columnsPerRow <= 3 ? this.columnsPerRow : 1;
  }
}
